import { debugEnabledByChannel } from '../channel';
import { getInstallId } from '../../telemetry/identity';
import { trackGateEvent } from '../../telemetry/events';
import { currentWorkerBase } from '../../telemetry/unlock';
import { readJsonPref, readPref, removePref, writeJsonPref, writePref } from '../prefs';

/**
 * Acquittement des achats Google Play (A118) : `POST {base}/v1/play/acknowledge`.
 *
 * Google rembourse un achat qui n'est pas acquitté (3 minutes pour un testeur de licence, 3 jours
 * pour un acheteur) et la TWA n'acquitte pas : c'est le Worker qui le fait, avec un compte de
 * service. Le jeu lui transmet le jeton d'achat après chaque achat (et pour les achats retrouvés
 * au lancement) et **réessaie** tant que le Worker n'a pas répondu définitivement.
 *
 * **Jamais bloquant, jamais bruyant** (A79) : une seule entrée dans la file locale
 * (`diceordie.play.ack`), aucune exception vers l'appelant, aucun message au joueur. Protocole :
 * `docs/TELEMETRY.md` §14.
 */

export const ACK_KEY = 'diceordie.play.ack';
/** Dernier jeton réglé (acquitté) : un achat restauré à chaque lancement n'est pas reposté à chaque fois. */
export const ACK_DONE_KEY = 'diceordie.play.ack.done';
export const ACK_PATH = '/v1/play/acknowledge';
export const ACK_TIMEOUT_MS = 10_000;
/** Premier délai de relance ; il double à chaque échec, jusqu'à `ACK_RETRY_MAX_MS`. */
export const ACK_RETRY_BASE_MS = 30_000;
export const ACK_RETRY_MAX_MS = 3_600_000;
/** Délai entre l'installation du module et la première tentative (hors du chemin de lancement). */
export const ACK_LAUNCH_DELAY_MS = 2000;

export interface AckEntry {
  /** Jeton d'achat Play à acquitter. */
  token: string;
  /** Tentatives déjà faites sans réponse définitive. */
  attempts: number;
  /** Date ISO à partir de laquelle on peut réessayer. */
  nextAt: string;
}

/** Délai avant la relance suivante : `min(30 s × 2^attempts, 1 h)`, `attempts` = essais déjà échoués. */
export function ackRetryDelay(attempts: number): number {
  const n = Math.max(0, Math.min(30, Math.floor(Number.isFinite(attempts) ? attempts : 0)));
  return Math.min(ACK_RETRY_BASE_MS * 2 ** n, ACK_RETRY_MAX_MS);
}

/** Lecture tolérante de l'entrée en attente : absente ou illisible → `null`. */
export function readAck(): AckEntry | null {
  const raw = readJsonPref<unknown>(ACK_KEY);
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.token !== 'string' || !o.token) return null;
  const attempts = typeof o.attempts === 'number' && Number.isFinite(o.attempts) && o.attempts >= 0 ? Math.floor(o.attempts) : 0;
  const nextAt = typeof o.nextAt === 'string' && !Number.isNaN(Date.parse(o.nextAt)) ? o.nextAt : new Date(0).toISOString();
  return { token: o.token.slice(0, 4096), attempts, nextAt };
}

function writeAck(entry: AckEntry): void {
  writeJsonPref(ACK_KEY, entry);
}

function clearAck(): void {
  removePref(ACK_KEY);
}

// ───────────────────────────────────────────────────────────────── Contexte

let levelOf: (() => number | undefined) | null = null;

/** Niveau courant du jeu, joint aux événements `ack_ok` / `ack_failed` (`installEntitlement`). */
export function configureAck(ctx: { level?: () => number | undefined }): void {
  levelOf = ctx.level ?? null;
}

function currentLevel(): number | undefined {
  try {
    return levelOf?.();
  } catch {
    return undefined;
  }
}

function debugLog(...args: unknown[]): void {
  try {
    if (debugEnabledByChannel()) console.debug('[acquittement Play]', ...args);
  } catch {
    /* le journal ne doit jamais gêner */
  }
}

// ───────────────────────────────────────────────────────────────────── Envoi

export type AckFetch = (
  url: string,
  init: {
    method: 'POST';
    headers: Record<string, string>;
    body: string;
    signal: AbortSignal;
    credentials: 'omit';
  },
) => Promise<{ ok: boolean; status: number; json?: () => Promise<unknown> }>;

export interface AckOptions {
  fetchFn?: AckFetch;
  /** Racine du Worker ; par défaut `currentWorkerBase()`. */
  base?: string | null;
  installId?: string;
  timeoutMs?: number;
  /** Heure courante (tests). */
  now?: number;
}

export type AckResult = 'none' | 'wait' | 'noUrl' | 'offline' | 'busy' | 'acked' | 'refused' | 'retry';

/** Raisons définitives du Worker (la réponse porte `ok: false` et l'une d'elles). */
const DEFINITIVE_REASONS = ['malformed', 'unknown', 'canceled', 'pending'] as const;

let retryTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;

/** Annule la relance en session (tests, débranchement). */
export function cancelRetry(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/** Une seule relance en session : la nouvelle remplace l'ancienne. */
function scheduleRetry(delayMs: number): void {
  cancelRetry();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushAck().catch(() => undefined);
  }, delayMs);
}

async function readBody(res: { json?: () => Promise<unknown> }): Promise<Record<string, unknown> | null> {
  try {
    const data = res.json ? await res.json() : null;
    return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function fail(entry: AckEntry, now: number): 'retry' {
  const next: AckEntry = { token: entry.token, attempts: entry.attempts + 1, nextAt: new Date(now + ackRetryDelay(entry.attempts)).toISOString() };
  // L'entrée a pu être remplacée par un achat plus récent pendant l'appel : on ne l'écrase pas.
  if (readAck()?.token === entry.token) writeAck(next);
  scheduleRetry(ackRetryDelay(entry.attempts));
  return 'retry';
}

/**
 * Envoie l'entrée en attente si son heure est venue. `200` : retirée, événement `ack_ok`. `400`,
 * `404`, `409` portant une raison du Worker : retirée, événement `ack_failed` (l'appareil reste
 * ouvert, A112-4). `429`, `503`, autres réponses, réseau, délai : gardée, relancée plus tard
 * (délai doublé, 1 h au plus, sans plafond d'essais). Ne lève jamais.
 */
export async function flushAck(opts: AckOptions = {}): Promise<AckResult> {
  try {
    const entry = readAck();
    if (!entry) return 'none';
    const now = opts.now ?? Date.now();
    const due = Date.parse(entry.nextAt);
    if (Number.isFinite(due) && due > now) {
      scheduleRetry(Math.min(due - now, ACK_RETRY_MAX_MS));
      return 'wait';
    }
    if (flushing) return 'busy';
    const base = opts.base === undefined ? currentWorkerBase() : opts.base;
    if (!base) return 'noUrl'; // pas d'adresse : on garde l'entrée sans rien tenter
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline'; // l'événement `online` relance
    flushing = true;
    try {
      const fetchFn: AckFetch = opts.fetchFn ?? ((url, init) => fetch(url, init as RequestInit) as unknown as ReturnType<AckFetch>);
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? ACK_TIMEOUT_MS);
      // Le délai couvre aussi la lecture du corps.
      const aborted = new Promise<never>((_, reject) => {
        ctl.signal.addEventListener('abort', () => reject(new Error('délai dépassé')));
      });
      aborted.catch(() => undefined);
      try {
        const work = (async (): Promise<AckResult> => {
          const res = await fetchFn(`${base}${ACK_PATH}`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ installId: opts.installId ?? getInstallId(), purchaseToken: entry.token }),
            signal: ctl.signal,
            credentials: 'omit',
          });
          if (res.status === 200) {
            const body = await readBody(res);
            const state = body && typeof body.state === 'string' ? body.state : 'acknowledged';
            if (readAck()?.token === entry.token) clearAck();
            writePref(ACK_DONE_KEY, entry.token);
            cancelRetry();
            trackGateEvent('ack_ok', { detail: state, level: currentLevel() });
            debugLog('acquitté', state);
            return 'acked';
          }
          if (res.status === 400 || res.status === 404 || res.status === 409) {
            const body = await readBody(res);
            const reason = body && body.ok === false && typeof body.reason === 'string' ? body.reason : '';
            // Un 404 sans raison est l'ancien Worker (route inconnue) : ce n'est pas un refus de Google.
            if ((DEFINITIVE_REASONS as readonly string[]).includes(reason)) {
              if (readAck()?.token === entry.token) clearAck();
              cancelRetry();
              trackGateEvent('ack_failed', { detail: reason, level: currentLevel() });
              debugLog('refusé', reason);
              return 'refused';
            }
          }
          debugLog('réponse', res.status);
          return fail(entry, opts.now ?? Date.now());
        })();
        return await Promise.race([work, aborted]);
      } catch {
        return fail(entry, opts.now ?? Date.now());
      } finally {
        clearTimeout(timer);
      }
    } finally {
      flushing = false;
    }
  } catch {
    return 'retry';
  }
}

/**
 * Met le jeton d'achat en file et tente tout de suite. Une seule entrée : un jeton plus récent
 * remplace l'ancien ; le même jeton garde son compte d'essais ; un jeton déjà acquitté n'est pas
 * reposté. Ne lève jamais.
 */
export function scheduleAck(token: string, opts: AckOptions = {}): void {
  try {
    if (typeof token !== 'string' || !token) return;
    if (readPref(ACK_DONE_KEY) === token) return;
    const current = readAck();
    if (!current || current.token !== token) {
      writeAck({ token: token.slice(0, 4096), attempts: 0, nextAt: new Date(opts.now ?? Date.now()).toISOString() });
    }
    void flushAck(opts).catch(() => undefined);
  } catch {
    /* l'acquittement ne doit jamais gêner le joueur (A79) */
  }
}

// ──────────────────────────────────────────────────────────────── Déclencheurs

let installed: { launch: ReturnType<typeof setTimeout>; onOnline: () => void } | null = null;

/**
 * Branche les tentatives de fond : une après `ACK_LAUNCH_DELAY_MS`, une au retour de la connexion
 * (`online`). Idempotent. Renvoie le débranchement (tests, `dispose`).
 */
export function installAckFlush(delayMs: number = ACK_LAUNCH_DELAY_MS): () => void {
  if (typeof window === 'undefined' || installed) return () => undefined;
  const run = (): void => {
    void flushAck().catch(() => undefined);
  };
  const launch = setTimeout(run, delayMs);
  try {
    window.addEventListener?.('online', run);
  } catch {
    /* fenêtre simplifiée (tests) */
  }
  const mine = { launch, onOnline: run };
  installed = mine;
  return () => {
    if (installed !== mine) return;
    clearTimeout(launch);
    try {
      window.removeEventListener?.('online', run);
    } catch {
      /* idem */
    }
    cancelRetry();
    installed = null;
  };
}
